// Trophy room — collapsible modal listing every catalog trophy with locked
// state. Anchored as a fixed overlay so it can appear on top of the CRT
// regardless of mode (TV / GUIDE / UPLOAD).
import React from "react";
import { Trophy, X } from "lucide-react";
import { TROPHIES, getUnlocked } from "@/lib/trophies";

export default function TrophyRoom({ open, onClose, refreshTick, overrides = {}, disabled = [] }) {
  const unlocked = React.useMemo(() => getUnlocked(), [refreshTick, open]);
  if (!open) return null;
  const disabledSet = new Set(disabled);
  const visibleTrophies = TROPHIES.filter((t) => !disabledSet.has(t.id));
  const total = visibleTrophies.length;
  const earned = visibleTrophies.filter((t) => unlocked.has(t.id)).length;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="trophy-room"
    >
      <div
        className="relative bg-black border-2 border-[var(--phosphor)] text-[var(--phosphor)] shadow-[0_0_30px_color-mix(in srgb, var(--phosphor) 45%, transparent)] w-full max-w-md"
        style={{ fontFamily: "VT323, monospace" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2 border-b border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)]">
          <div className="flex items-center gap-2 chromatic tracking-[0.25em] text-base">
            <Trophy size={16} /> TROPHY ROOM
          </div>
          <button onClick={onClose} className="hover:opacity-80" data-testid="trophy-room-close">
            <X size={16} />
          </button>
        </div>
        <div className="px-4 py-2 text-xs opacity-70 tracking-widest border-b border-[color-mix(in_srgb,var(--phosphor)_20%,transparent)]">
          {earned} / {total} EARNED
        </div>
        <ul className="px-4 py-2 max-h-[60vh] overflow-y-auto space-y-1.5">
          {visibleTrophies.map((t) => {
            const earnedIt = unlocked.has(t.id);
            const ov = overrides[t.id] || {};
            const displayName = ov.name || t.name;
            const displayDesc = ov.desc || t.desc;
            return (
              <li
                key={t.id}
                data-testid={`trophy-${t.id}`}
                className={`border px-2 py-1.5 ${
                  earnedIt
                    ? "border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_10%,transparent)]"
                    : "border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] opacity-50"
                }`}
              >
                <div className="flex items-center gap-2 text-sm tracking-widest">
                  <Trophy size={14} className={earnedIt ? "" : "opacity-40"} />
                  <span>{displayName}</span>
                  {!earnedIt && <span className="ml-auto text-xs opacity-60">LOCKED</span>}
                </div>
                <div className="text-xs opacity-75 mt-0.5 leading-snug">
                  {displayDesc}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
